import { FigureFileType, UploadGuideFigureFileMutation } from '../../../__generated__/graphql';
import { Unpacked } from '../../../graphQLTypes/types';
import { ImageFigureAttributes } from '../tiptap/extensions/plugins/ImageFigure';
import { Editor } from '@tiptap/core';
import { Config } from '../../../env';
import { getNodesOfType, updateAllMatchingNodeAttributes } from '../tiptap/tiptapHelper';
import { NumberedNodes } from '../tiptap/numbering';

export type FigureFile = NonNullable<Unpacked<UploadGuideFigureFileMutation>['guideUploadFigureFile']['figureFile']>;

export interface ImageFigure {
  figureId: string;
  files: FigureFile[];
}

export interface FigureInstance {
  nodeId: string;
  numbering: string;
}

export interface ImageFigureWithInstances extends ImageFigure {
  instances: FigureInstance[];
}

export const getFileNames = (imageFigure: ImageFigure): { small?: string; large?: string; original?: string } => {
  const small = imageFigure.files.find((f) => f.type === FigureFileType.Small)?.fileName ?? undefined;
  const large = imageFigure.files.find((f) => f.type === FigureFileType.Large)?.fileName ?? undefined;
  const original = imageFigure.files.find((f) => f.type === FigureFileType.Original)?.fileName ?? undefined;
  return { small, large, original };
};

export const getMissingFileWarning = (imageFigure: ImageFigure): string | null => {
  const hasSmall = imageFigure.files.some((f) => f.type === FigureFileType.Small);
  const hasLarge = imageFigure.files.some((f) => f.type === FigureFileType.Large);
  if (!hasSmall && !hasLarge) return 'Mangler bilde';
  if (!hasSmall) return 'Mangler lite bilde';
  if (!hasLarge) return 'Mangler stort bilde';
  return null;
};

export const getThumbnailUrlFromImageFigure = (config: Config | undefined, guideId: string, imageFigure: ImageFigure) => {
  const file = imageFigure.files.find((file) => file.type === FigureFileType.Small);
  if (!file) {
    return undefined;
  }
  return getFigureFileUrl(config, guideId, imageFigure.figureId, file);
};

export const getFigureFileUrl = (config: Config | undefined, guideId: string, figureId: string, file: FigureFile) => {
  if (!config) {
    return undefined;
  }
  return `${config.apiUrl}/guides/${guideId}/figures/${figureId}/files/${file.id}/${file.fileName}?type=${file.type.toLowerCase()}`;
};

export const getImageFigures = (editor: Editor): ImageFigure[] => {
  const imageFigureNodes = getNodesOfType(editor.getJSON(), 'imageFigure');
  // Only unique figures by figureId. Do not duplicate if there are multiple nodes with the same figureId
  return imageFigureNodes
    .map((node) => mapImageFigureFromAttributes(node.attrs as ImageFigureAttributes))
    .filter((figure, index, self) => self.findIndex((f) => f.figureId === figure.figureId) === index);
};

export const getImageFiguresWithInstances = (editor: Editor): ImageFigureWithInstances[] => {
  const imageFigureNodes = getNodesOfType(editor.getJSON(), 'imageFigure');
  const numberedNodes = (editor.storage['numberedNodes'] as NumberedNodes | undefined) ?? new Map();

  const figureMap = new Map<string, ImageFigureWithInstances>();
  for (const node of imageFigureNodes) {
    const attrs = node.attrs as ImageFigureAttributes;
    const figureId = attrs.figureId ?? '';
    if (!figureMap.has(figureId)) {
      const figure = mapImageFigureFromAttributes(attrs);
      figureMap.set(figureId, { ...figure, instances: [] });
    }
    const nodeId = attrs.id;
    if (nodeId) {
      const numbered = numberedNodes.get(nodeId);
      figureMap.get(figureId)!.instances.push({
        nodeId,
        numbering: numbered?.numbering ?? '',
      });
    }
  }

  return Array.from(figureMap.values());
};

export const mapImageFigureFromAttributes = (attrs: ImageFigureAttributes): ImageFigure => {
  return { figureId: attrs.figureId ?? '', files: getFilesFromImageFigureAttributes(attrs) };
};

export const mapToAttributesFromImageFigure = (id: string, imageFigure: ImageFigure): ImageFigureAttributes => {
  const attrs: ImageFigureAttributes = {
    id,
    figureId: imageFigure.figureId,
    smallId: null,
    smallFilename: null,
    smallMetadata: null,
    largeId: null,
    largeFilename: null,
    largeMetadata: null,
    originalId: null,
    originalFilename: null,
    originalMetadata: null,
  };
  for (const file of imageFigure.files) {
    const metadata = file.metadata
      ?.filter((m) => !!m)
      .map((m) => `${m.name}:${m.value}`)
      .join(';');
    if (file.type === FigureFileType.Small) {
      attrs.smallId = file.id;
      attrs.smallFilename = file.fileName || null;
      attrs.smallMetadata = metadata || null;
    }
    if (file.type === FigureFileType.Large) {
      attrs.largeId = file.id;
      attrs.largeFilename = file.fileName || null;
      attrs.largeMetadata = metadata || null;
    }
    if (file.type === FigureFileType.Original) {
      attrs.originalId = file.id;
      attrs.originalFilename = file.fileName || null;
      attrs.originalMetadata = metadata || null;
    }
  }
  return attrs as ImageFigureAttributes;
};

const getMetadata = (metadata: string | null): FigureFile['metadata'] => {
  const metadataParts = metadata?.split(';') || [];
  const metadataArray = metadataParts.map((part) => {
    const [name, value] = part.split(':');
    return { name, value };
  });
  return metadataArray;
};

const getFilesFromImageFigureAttributes = (attrs: ImageFigureAttributes): FigureFile[] => {
  const files: FigureFile[] = [];
  if (attrs.smallId) {
    files.push({
      id: attrs.smallId,
      figureId: attrs.figureId,
      fileName: attrs.smallFilename,
      metadata: getMetadata(attrs.smallMetadata),
      type: FigureFileType.Small,
    });
  }
  if (attrs.largeId) {
    files.push({
      id: attrs.largeId,
      figureId: attrs.figureId,
      fileName: attrs.largeFilename,
      metadata: getMetadata(attrs.largeMetadata),
      type: FigureFileType.Large,
    });
  }
  if (attrs.originalId) {
    files.push({
      id: attrs.originalId,
      figureId: attrs.figureId,
      fileName: attrs.originalFilename,
      metadata: getMetadata(attrs.originalMetadata),
      type: FigureFileType.Original,
    });
  }
  return files;
};

export const updateImageFigureInEditor = (editor: Editor, imageFigure: ImageFigure) => {
  const nodes = getNodesOfType(editor.getJSON(), 'imageFigure');
  const existingAttributes = nodes.map((n) => n.attrs as ImageFigureAttributes).filter((attr) => attr.figureId === imageFigure.figureId);
  existingAttributes.forEach((attrs) => {
    if (attrs.id) {
      const newAttributes = mapToAttributesFromImageFigure(attrs.id, imageFigure);
      updateAllMatchingNodeAttributes(editor, 'imageFigure', (a) => (a as ImageFigureAttributes).id === attrs.id, newAttributes);
    }
  });
};
